import React from 'react';
import { Crown, MapPin, DollarSign, Heart, User, Edit, Calendar, Star } from 'lucide-react';

const ProfileHeader = ({ profileData, subscription, onEdit }) => {
    const {
        name = 'Utilizator',
        age,
        sex,
        sexualOrientation,
        city,
        income,
        photos = [],
        visibility = {}
    } = profileData;

    const profilePhoto = photos[0] || null;

    const formatIncome = (income) => {
        const incomeRanges = {
            '0-2000': '0 - 2,000 EUR',
            '2000-4000': '2,000 - 4,000 EUR',
            '4000-6000': '4,000 - 6,000 EUR',
            '6000+': '6,000+ EUR'
        };
        return incomeRanges[income] || income;
    };

    const getSexLabel = (sex) => {
        return sex === 'male' ? 'Bărbat' : sex === 'female' ? 'Femeie' : '';
    };

    const getOrientationLabel = (orientation) => {
        const labels = {
            'heterosexual': 'Heterosexual',
            'bisexual': 'Bisexual',
            'gay': 'Gay',
            'lesbian': 'Lesbiană'
        };
        return labels[orientation] || orientation;
    };

    const getSubscriptionBadge = () => {
        if (subscription.type === 'premium') {
            return (
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    <Crown size={16} />
                    Premium
                </div>
            );
        } else if (subscription.type === 'standard') {
            return (
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    <Star size={16} />
                    Standard
                </div>
            );
        }
        return null;
    };

    return (
        <div className="max-w-4xl mx-auto">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                {/* Header with Photo */}
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 h-40"></div>

                <div className="relative px-8 pb-8">
                    {/* Profile Photo & Actions */}
                    <div className="flex justify-between items-start -mt-20 mb-6">
                        {/* Profile Photo - FĂRĂ BADGE */}
                        <div className="relative">
                            <div className="w-40 h-40 rounded-full border-4 border-white shadow-xl bg-white overflow-hidden">
                                {profilePhoto ? (
                                    <img
                                        src={profilePhoto}
                                        alt="Profil"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100">
                                        <User size={60} className="text-slate-400" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Edit Button + Badge - POZIȚIONARE CORECTĂ */}
                        <div className="mt-20 flex flex-col items-end gap-3">
                            <button
                                onClick={onEdit}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition shadow-md"
                            >
                                <Edit size={20} />
                                Editează Profil
                            </button>

                            {/* Badge LÂNGĂ buton, NU pe poză */}
                            {getSubscriptionBadge()}
                        </div>
                    </div>

                    {/* Profile Info */}
                    <div className="space-y-4">
                        {/* Name and Age */}
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 mb-2">
                                {name}
                                {visibility.age && age && (
                                    <span className="text-2xl text-slate-600 ml-2">{age} ani</span>
                                )}
                            </h1>
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            {/* Sex */}
                            {visibility.sex && sex && (
                                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                        <User size={20} className="text-blue-600" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-slate-500 uppercase">Sex</div>
                                        <div className="text-lg font-semibold text-slate-900">{getSexLabel(sex)}</div>
                                    </div>
                                </div>
                            )}

                            {/* Sexual Orientation */}
                            {visibility.sexualOrientation && sexualOrientation && (
                                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                                        <Heart size={20} className="text-pink-600" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-slate-500 uppercase">Orientare</div>
                                        <div className="text-lg font-semibold text-slate-900">{getOrientationLabel(sexualOrientation)}</div>
                                    </div>
                                </div>
                            )}

                            {/* City */}
                            {visibility.city && city && (
                                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <MapPin size={20} className="text-green-600" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-slate-500 uppercase">Oraș</div>
                                        <div className="text-lg font-semibold text-slate-900">{city}</div>
                                    </div>
                                </div>
                            )}

                            {/* Income */}
                            {visibility.income && income && (
                                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                        <DollarSign size={20} className="text-yellow-600" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-slate-500 uppercase">Venituri</div>
                                        <div className="text-lg font-semibold text-slate-900">{formatIncome(income)}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Photo Gallery */}
                        {photos.length > 1 && (
                            <div className="mt-8">
                                <h3 className="text-lg font-semibold text-slate-900 mb-4">Galerie Foto</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {photos.slice(1).map((photo, index) => (
                                        <div key={index} className="aspect-square rounded-xl overflow-hidden border-2 border-slate-200 hover:border-blue-500 transition cursor-pointer">
                                            <img
                                                src={photo}
                                                alt={`Fotografie ${index + 2}`}
                                                className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {!name && !age && !sex && !city && (
                            <div className="text-center py-12">
                                <User size={48} className="mx-auto text-slate-300 mb-4" />
                                <p className="text-slate-600 mb-4">Profilul tău este gol</p>
                                <button
                                    onClick={onEdit}
                                    className="text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    Completează profilul →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Subscription Info Card */}
            {subscription.type !== 'none' && subscription.endDate && (
                <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                                <Crown size={24} className="text-white" />
                            </div>
                            <div>
                                <div className="font-semibold text-blue-900">
                                    Abonament {subscription.type === 'premium' ? 'Premium' : 'Standard'} Activ
                                </div>
                                <div className="text-sm text-blue-700">
                                    Expiră la: {subscription.endDate.toLocaleDateString('ro-RO', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-blue-700">
                            <Calendar size={16} />
                            {Math.ceil((subscription.endDate - new Date()) / (1000 * 60 * 60 * 24))} zile rămase
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileHeader;